import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Play, CheckCircle2, AlertCircle, Volume2, VolumeX } from 'lucide-react';
import { cn } from '@/lib/utils';

// Video Embed Komponenti - Vimeo, YouTube ve Cloudflare Stream desteği
const VideoLesson = ({ 
  title,
  videoUrl,
  videoType = 'youtube', // 'youtube', 'vimeo', 'cloudflare', 'embed'
  description,
  questions = [],
  onAnswer,
  disabled = false 
}) => {
  const [videoWatched, setVideoWatched] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);

  // Video URL'ini embed formatına çevir
  const getEmbedUrl = () => {
    if (!videoUrl) return null;

    // Zaten embed URL ise direkt kullan
    if (videoType === 'embed' || videoUrl.includes('embed')) {
      return videoUrl;
    }

    // YouTube
    if (videoType === 'youtube' || videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      const videoId = videoUrl.includes('youtu.be') 
        ? videoUrl.split('/').pop() 
        : videoUrl.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}?rel=0`;
    }

    // Vimeo
    if (videoType === 'vimeo' || videoUrl.includes('vimeo.com')) {
      const videoId = videoUrl.split('/').pop();
      return `https://player.vimeo.com/video/${videoId}?badge=0&autopause=0&player_id=0`;
    }

    // Cloudflare Stream
    if (videoType === 'cloudflare' || videoUrl.includes('cloudflarestream.com') || videoUrl.includes('videodelivery.net')) {
      const videoId = videoUrl.split('/').find(part => part.length > 20);
      return `https://iframe.videodelivery.net/${videoId}`;
    }

    return videoUrl;
  };

  const embedUrl = getEmbedUrl();

  const handleVideoEnd = () => {
    setVideoWatched(true);
  };

  const handleAnswerSelect = (index) => {
    if (disabled || showResult) return;
    setSelectedAnswer(index);
    setShowResult(true);

    const question = questions[currentQuestion];
    const isCorrect = index === question.correct;
    
    // Kısa gecikme sonrası sonucu bildir
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        // Sonraki soruya geç
        setCurrentQuestion(prev => prev + 1);
        setSelectedAnswer(null);
        setShowResult(false);
      } else {
        // Tüm sorular tamamlandı
        onAnswer?.(isCorrect);
      }
    }, 1500);
  };

  const handleSkipVideo = () => {
    setVideoWatched(true);
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* Video Başlığı */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">{title || 'Video Dersi'}</h2>
        {description && (
          <p className="text-gray-600">{description}</p>
        )}
      </div>

      {/* Video Alanı */}
      {!videoWatched ? (
        <div className="mb-6">
          {embedUrl ? (
            <div className="relative w-full bg-black rounded-2xl overflow-hidden shadow-xl" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={embedUrl}
                title={title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="w-full aspect-video bg-gray-100 rounded-2xl flex items-center justify-center border-2 border-dashed border-gray-300">
              <div className="text-center p-8">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Video yüklenemedi</p>
                <p className="text-sm text-gray-400 mt-2">Lütfen internet bağlantınızı kontrol edin</p>
              </div>
            </div>
          )}

          {/* Video Tamamlandı Butonu */}
          <div className="mt-4 flex justify-center gap-4">
            <Button 
              onClick={handleSkipVideo}
              variant="outline"
              className="px-6 py-3 rounded-xl border-gray-300"
            >
              Atla
            </Button>
            <Button 
              onClick={() => setVideoWatched(true)}
              className="px-6 py-3 rounded-xl bg-blue-500 hover:bg-blue-600"
            >
              <CheckCircle2 className="w-5 h-5 mr-2" />
              Videoyu İzledim
            </Button>
          </div>
        </div>
      ) : questions.length > 0 ? (
        /* Video Sonrası Sorular */
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <div className="mb-4">
            <span className="text-sm text-blue-500 font-medium">Soru {currentQuestion + 1}/{questions.length}</span>
          </div>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-6">
            {questions[currentQuestion]?.question}
          </h3>

          <div className="space-y-3">
            {questions[currentQuestion]?.options.map((option, idx) => {
              const isSelected = selectedAnswer === idx;
              const isCorrect = idx === questions[currentQuestion]?.correct;
              const showCorrect = showResult && isCorrect;
              const showWrong = showResult && isSelected && !isCorrect;

              return (
                <button
                  key={idx}
                  onClick={() => handleAnswerSelect(idx)}
                  disabled={disabled || showResult}
                  className={cn(
                    'w-full p-4 rounded-xl text-left transition-all border-2',
                    !showResult && !isSelected && 'border-gray-200 hover:border-blue-300 hover:bg-blue-50',
                    !showResult && isSelected && 'border-blue-500 bg-blue-50',
                    showCorrect && 'border-green-500 bg-green-50',
                    showWrong && 'border-red-500 bg-red-50',
                    (disabled || showResult) && 'cursor-not-allowed'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className={cn(
                      'font-medium',
                      showCorrect && 'text-green-700',
                      showWrong && 'text-red-700',
                      !showResult && 'text-gray-700'
                    )}>
                      {option}
                    </span>
                    {showCorrect && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                    {showWrong && <AlertCircle className="w-5 h-5 text-red-500" />}
                  </div>
                </button>
              );
            })}
          </div>

          {showResult && (
            <div className={cn(
              'mt-4 p-4 rounded-xl',
              selectedAnswer === questions[currentQuestion]?.correct
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            )}>
              <p className={cn(
                'font-medium',
                selectedAnswer === questions[currentQuestion]?.correct
                  ? 'text-green-700'
                  : 'text-red-700'
              )}>
                {selectedAnswer === questions[currentQuestion]?.correct
                  ? '✅ Doğru cevap!'
                  : '❌ Yanlış cevap.'}
              </p>
              {questions[currentQuestion]?.explanation && (
                <p className="text-sm text-gray-600 mt-1">
                  {questions[currentQuestion].explanation}
                </p>
              )}
            </div>
          )}
        </div>
      ) : (
        /* Soru yoksa direkt geç */
        <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-green-700 mb-2">Video Tamamlandı!</h3>
          <p className="text-green-600">Devam etmek için aşağıdaki butona tıklayın.</p>
          <Button 
            onClick={() => onAnswer?.(true)}
            className="mt-4 px-8 py-3 bg-green-500 hover:bg-green-600 rounded-xl"
          >
            Devam Et
          </Button>
        </div>
      )}
    </div>
  );
};

export default VideoLesson;
